import {
  StoreInterfaceType,
  StoreSetStateOptionsType,
  StoreSetStateType,
  StoreStateType
} from '@clientos/types';
import IStoreService from '../../../services/storeService/IStoreService';
import { getServices } from '../../../infra/startup';
import bindAllMethods from '../../../utils/bindAllMethods';

/**
 * The Store state contains a map with a string as a key and accepts any
 * value. The data storaged only could be changed using setState.
 * These values are cleaned when the page is refreshed.
 */
class StoreInterfaceV1 {
  private _storeService: IStoreService;

  constructor() {
    const services = getServices();
    this._storeService = services.storeService;
    bindAllMethods(this);
  }

  get state(): StoreStateType {
    return this._storeService.state;
  }

  /**
   * Used to safely change state values. The setState doesn't changing
   * the values that have not been informed to it, thus providing
   * greater security in the manipulation of states.
   *
   * @param {Object | Function} data - the data to be updated
   * @param {Object} options - Options to change the setState behavior
   * @param {boolean} options.deepSafeSetValue - Is true by default. If true,
   * it provides greater security in the manipulation of states, goes through
   * all the new value, maintaining old properties that have not been
   * informed. If you want to use this option and want to delete an existing
   * property, enter it as undefined to be removed from the state.
   * @returns {void}
   */
  setState(data: StoreSetStateType, options?: StoreSetStateOptionsType): void {
    this._storeService.setState(data, options);
  }

  /**
   * dangerousSetState should be used only if you are sure of what you are
   * doing. The value entered will be the new global state. If you know React,
   * this method works identical to the setState of the useState hook.
   *
   * @param  {Object | Function} data - the data to be updated
   * @returns {void}
   */
  dangerousSetState(data: StoreSetStateType): void {
    this._storeService.dangerousSetState(data);
  }

  /***
   * This function adds an observer to the store. It is necessary to inform
   *  a string as identification (id), so that you can remove it when you are
   * no longer using it. The observer will be notified through its signature
   * whenever the state is changed, passing the new state value as an argument.
   *
   * @param {string} id - The identifier for the observer.
   * @param  {Function} signature - Method to be called when the state changed
   * @returns {void}
   */
  addObserver(
    id: string,
    signature: (state: StoreStateType, previousState?: StoreStateType) => void
  ): void {
    this._storeService.addObserver(id, signature);
  }

  /**
   * This removes the signature of the observer by the given id.
   *
   * @param {string} id - An observer identifier.
   * @returns {void}
   */
  removeObserver(id: string): void {
    this._storeService.removeObserver(id);
  }

  /**
   * The store has an internal counter, that whenever an id is generated
   * through this function, is added 1 to that counter, helping to generate a
   * unique id;
   *
   * @returns {string}
   */
  generateId(): string {
    return this._storeService.generateId();
  }

  /**
   * Used in React components only. It is a customized hook that must receive
   * the React object itself as an argument. It returns the store itself and
   * consequently update the component whenever the state is changed;
   *
   * @param {Object} React - the React object
   */
  useReactStore(React: any): any {
    return this._storeService.useReactStore(React);
  }

  public getInterface(): StoreInterfaceType {
    return {
      state: this.state,
      setState: this.setState,
      dangerousSetState: this.dangerousSetState,
      addObserver: this.addObserver,
      removeObserver: this.removeObserver,
      generateId: this.generateId,
      useReactStore: this.useReactStore
    };
  }
}

export default StoreInterfaceV1;
